import React, { useState, useEffect, useCallback } from "react";
import { apiRequest } from "../utils/api";
import { useAlert } from '../components/AlertModal';
import "../styles/Scrape.css";

const Scrape = () => {
    const [baseUrl, setBaseUrl] = useState("");
    const [newItems, setNewItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [categories, setCategories] = useState([]);
    const [taskId, setTaskId] = useState(() => localStorage.getItem("scrapeTaskId") || null);
    const [progress, setProgress] = useState(() => {
        const stored = localStorage.getItem("scrapeProgress");
        return stored !== null ? Number(stored) : null;
    });
    const { showAlert, showConfirm } = useAlert();

    const fetchNewItems = useCallback(async () => {
        try {
            const data = await apiRequest("inventory/newitems/", "GET");
            setNewItems(data);
        } catch (error) {
            console.error("データ取得エラー:", error);
        }
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await apiRequest("inventory/categorys/", "GET");
            setCategories(response);
        } catch (error) {
            console.error("カテゴリー取得失敗:", error);
        }
    }, []);

    useEffect(() => {
        fetchNewItems();
        fetchCategories();
    }, [fetchNewItems, fetchCategories]);

    const resetScrapeState = useCallback(() => {
        localStorage.removeItem("scrapeTaskId");
        localStorage.removeItem("scrapeProgress");
        setTaskId(null);
        setProgress(null);
    }, []);

    const pollProgress = useCallback((taskId) => {
        const interval = setInterval(async () => {
            try {
                const res = await apiRequest(`inventory/scrape_progress/?task_id=${taskId}`, "GET");
                const progressValue = res.progress;
                setProgress(progressValue);
                localStorage.setItem("scrapeProgress", progressValue);

                if (progressValue >= 100 || progressValue === -1) {
                    clearInterval(interval);
                    const message = progressValue === 100
                        ? "スクレイピング完了！OKを押すとリセットされます。"
                        : "スクレイピング中にエラーが発生しました。";

                    await showAlert(message);
                    resetScrapeState();
                    await fetchNewItems();
                }
            } catch (error) {
                console.error("進捗確認エラー:", error);
                clearInterval(interval);
            }
        }, 2000);
    }, [showAlert, resetScrapeState, fetchNewItems]);

    useEffect(() => {
        if (taskId && (progress === null || progress < 100)) {
            pollProgress(taskId);
        }
    }, [taskId, progress, pollProgress]);

    const handleScrape = async (e) => {
        e.preventDefault();
        try {
            const res = await apiRequest("inventory/scrape/", "POST", { baseurl: baseUrl });
            localStorage.setItem("scrapeTaskId", res.task_id);
            localStorage.setItem("scrapeProgress", "0");
            setTaskId(res.task_id);
            setProgress(0);
            showAlert("スクレイピングを非同期で開始しました。進捗を確認中…");
        } catch (error) {
            console.error("スクレイピングエラー:", error);
            showAlert("スクレイピング開始に失敗しました。");
        }
    };

    const handleCancel = () => {
        resetScrapeState();
        showAlert("スクレイピング状態をリセットしました。");
    };

    const handleSelect = (id) => {
        setSelectedItems((prev) => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    const handleEdit = async (id, field, value) => {
        setNewItems((prevItems) =>
            prevItems.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
        try {
            await apiRequest(`inventory/newitems/${id}/`, "PATCH", { [field]: value });
            fetchNewItems();
        } catch (error) {
            console.error("更新エラー:", error);
        }
    };

    const handleDelete = async (id) => {
        const confirmed = await showConfirm("この商品を削除しますか？");
        if (!confirmed) return;

        try {
            await apiRequest(`inventory/newitems/${id}/delete_with_image/`, "DELETE");
            setNewItems((prev) => prev.filter((item) => item.id !== id));
            showAlert("削除しました");
        } catch (error) {
            console.error("削除エラー:", error);
            showAlert("削除に失敗しました");
        }
    };

    const handleRegister = async () => {
        const selectedData = newItems.filter((item) => selectedItems.has(item.id));
        if (selectedData.length === 0) {
            showAlert("登録する商品がありません。");
            return;
        }
        try {
            const response = await apiRequest("inventory/register_inventory/", "POST", { items: selectedData });
            showAlert(response.message);
            setNewItems((prev) => prev.filter((item) => !selectedItems.has(item.id)));
            setSelectedItems(new Set());
        } catch (error) {
            console.error("登録エラー:", error);
        }
    };

    return (
        <div className="scrape-all-container">
            <h1>一括登録（HOBBY SEARCH）</h1>
            <form onSubmit={handleScrape}>
                <label>URL:</label>
                <input name="scrape_baseurl" type="text" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required />
                <button type="submit" disabled={progress !== null && progress < 100}>スクレイピング開始</button>
                {progress !== null && (
                    <button type="button" name="scrape_cancel" onClick={handleCancel} style={{ marginLeft: 10 }}>
                        中止・リセット
                    </button>
                )}
            </form>

            {progress !== null && (
                <div style={{ marginTop: 10 }}>
                    <p>進捗: {progress}%</p>
                    <progress name="scrape_progressbar" value={progress} max="100" style={{ width: "100%" }} />
                </div>
            )}

            <p>除外メーカー</p>
            <ul>
                <li>full FOTS JAPAN</li>
                <li>insight</li>
            </ul>

            <div className="newitems-container">
                <table className="newitems-table">
                    <thead>
                        <tr>
                            <th>登録</th><th>発売日</th><th>画像</th><th>SKU</th><th>商品名</th>
                            <th>メーカー</th><th>JANコード</th><th>タイトル</th>
                            <th>ジャンル1</th><th>カテゴリー</th><th>削除</th>
                        </tr>
                    </thead>
                    <tbody>
                        {newItems.length > 0 ? (
                            newItems.map((item) => (
                                <tr key={item.id}>
                                    <td><input name="scrape_check" type="checkbox" checked={selectedItems.has(item.id)} onChange={() => handleSelect(item.id)} /></td>
                                    <td><input name="scrape_release" type="date" value={item.new_release_date || ""} onChange={(e) => handleEdit(item.id, "new_release_date", e.target.value)} /></td>
                                    <td><img src={`/media/product_images/${item.new_asin}.jpg`} alt="商品画像" height="30" /></td>
                                    <td><input name="scrape_asin" type="text" value={item.new_asin || ""} onChange={(e) => handleEdit(item.id, "new_asin", e.target.value)} /></td>
                                    <td><input name="scrape_name" type="text" value={item.new_item_name} onChange={(e) => handleEdit(item.id, "new_item_name", e.target.value)} /></td>
                                    <td><input name="scrape_maker" type="text" value={item.new_manufacturer || ""} onChange={(e) => handleEdit(item.id, "new_manufacturer", e.target.value)} /></td>
                                    <td><input name="scrape_jan" type="text" value={item.new_jan || ""} onChange={(e) => handleEdit(item.id, "new_jan", e.target.value)} /></td>
                                    <td><input name="scrape_title" type="text" value={item.new_title || ""} onChange={(e) => handleEdit(item.id, "new_title", e.target.value)} /></td>
                                    <td><input name="scrape_genre1" type="text" value={item.new_genre1 || ""} onChange={(e) => handleEdit(item.id, "new_genre1", e.target.value)} /></td>
                                    <td>
                                        <select
                                            name="scrape_category"
                                            value={item.new_category?.id || ""}
                                            onChange={(e) =>
                                                handleEdit(item.id, "new_category_id", Number(e.target.value))
                                            }
                                        >
                                            <option value="">選択してください</option>
                                            {categories.map((category) => (
                                                <option key={category.id} value={category.id}>
                                                    {category.category_name}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td><button onClick={() => handleDelete(item.id)}>×</button></td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="11">データがありません。</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <button name="scrape_refresh" onClick={fetchNewItems}>最新データを取得</button>
            <div style={{ marginTop: 10 }}>
                <button name="scrape_select_all" onClick={() => {
                    const allIds = newItems.map((item) => item.id);
                    setSelectedItems(new Set(allIds));
                }}>
                    すべて選択
                </button>

                <button name="scrape_deselect_all" onClick={() => {
                    setSelectedItems(new Set());
                }} style={{ marginLeft: 10 }}>
                    選択解除
                </button>

                <button name="scrape_register" onClick={handleRegister} style={{ marginLeft: 20 }}>
                    選択した商品を登録
                </button>
            </div>
        </div>
    );
};

export default Scrape;
